import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ExternalLink, Sparkles } from 'lucide-react';

export default function ExperimentalFeatures() {
  const features = [
    {
      title: 'Emergent Empathy Mobile App',
      icon: '💙',
      description: 'Step-by-step Blue→Red→Green→Gold flow for urgent emotional communication. Perfect for hard moments.',
      link: '/emergent-empathy',
      color: 'from-blue-600 to-emerald-500'
    },
    {
      title: 'Constellation Work',
      icon: '🌌',
      description: 'Family constellation tool for exploring relationship dynamics and systemic patterns.',
      link: '/constellation',
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Find Practice Partners',
      icon: '🤝',
      description: 'Connect with other members for practice sessions and mutual support.',
      link: '/partners',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Planetary Fam Matching',
      icon: '🌍',
      description: 'Match with compatible families and communities for deeper connections.',
      link: '/planetary',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Interactive Workbook (Original)',
      icon: '📖',
      description: 'The original interactive workbook with additional features and tools.',
      link: '/workbook',
      color: 'from-indigo-500 to-purple-500'
    },
    {
      title: 'OFNR Practice Games',
      icon: '🎮',
      description: 'Interactive games and exercises from the Belonging Sessions Trainer.',
      link: '/ofnr-trainer',
      color: 'from-amber-500 to-orange-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-amber-100 to-orange-100 border-l-4 border-amber-600 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-amber-900 mb-2">🔬 Experimental Features</h2>
        <p className="text-amber-800">
          Advanced practice tools and experimental features for deepening connection and community building.
          These tools are continuously evolving based on user feedback.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, idx) => (
          <Card key={idx} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-400">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className={`text-5xl bg-gradient-to-r ${feature.color} text-transparent bg-clip-text`}>
                  {feature.icon}
                </div>
                <span className="text-lg">{feature.title}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700 leading-relaxed text-sm">
                {feature.description}
              </p>
              <a href={feature.link} target="_blank" rel="noopener noreferrer">
                <Button className={`w-full bg-gradient-to-r ${feature.color} hover:opacity-90 transition-opacity`}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Launch Tool
                </Button>
              </a>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          About These Tools
        </h3>
        <p className="text-blue-800 text-sm leading-relaxed">
          These experimental features represent innovative approaches to practicing NVC and building belonging. 
          They combine technology with compassionate communication to create new possibilities for connection.
          We welcome your feedback as we continue to develop these tools.
        </p>
      </div>
    </div>
  );
}
